import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import {getProductId, useProductCompare} from '../product-compare.store';
import {createPortal} from "react-dom";
import { productImageSrc } from '$utils/product-image-src';
import Link from "next/link";
import {IProduct} from "~/lib/data-contract";
import ProductCompareModal from "$features/product-compare/components/product-compare-modal";
import {Button} from "$shared/components/button";

const getProductImage = (product: any): string | undefined => {
    const image =
        product?.photoPath ||
        product?.imageUrl;

    return image
        ? productImageSrc(image)
        : undefined;
};

const getProductTitle = (product: any): string =>
    product?.productName ||
    product?.description1 ||
    product?.highlights?.productName ||
    product?.description2 ||
    'Product';

const getProductUrl = (product: any): string | undefined =>
    product?.canonicalProductUrl ||
    product?.url ||
    (product?.sanitizedItemNumber
        ? `/${product.sanitizedItemNumber}`
        : undefined);

export const CompareTray = () => {
    const {
        products,
        removeProduct,
        isTrayVisible,
        dismissTray,
        isCompareModalOpen,
        openCompareModal,
        closeCompareModal,
    } = useProductCompare();

    const [hasMounted, setHasMounted] =
        useState(false);

    const [resolvedProducts, setResolvedProducts] =
        useState<IProduct[]>([]);

    const [isLoadingCompare, setIsLoadingCompare] =
        useState(false);

    const removeComparedProduct = (
        product: IProduct
    ) => {
        const productId = getProductId(product);
        removeProduct(product);
        setResolvedProducts((currentProducts) =>
            currentProducts.filter(
                (currentProduct) =>
                    getProductId(currentProduct) !==
                    productId
            )
        );
    };

    useEffect(() => {
        setHasMounted(true);
    }, []);

    if (!hasMounted || !products.length) {
        return null;
    }

    const openCompare = async () => {
        const itemNumbers = products
            .map(getProductId)
            .filter(Boolean)
            .join(',');

        if (!itemNumbers || isLoadingCompare) {
            return;
        }

        try {
            setIsLoadingCompare(true);
            const response = await fetch(
                `/api/product-compare?itemNumbers=${encodeURIComponent(
                    itemNumbers
                )}`
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to load comparison products: ${response.status}`
                );
            }

            const fetchedProducts =
                await response.json();

            setResolvedProducts(fetchedProducts);
            openCompareModal();
        } catch (error) {
            console.error(
                'Failed to load comparison products',
                error
            );
        } finally {
            setIsLoadingCompare(false);
        }
    };

    return createPortal(
        <>
            {isTrayVisible && !isCompareModalOpen && (
                <Wrapper>
            <TopRow>
                <Title>
                    Compare products
                    <Count>
                        {products.length}/4
                    </Count>
                </Title>

                <CloseButton
                    type="button"
                    onClick={dismissTray}
                    aria-label="Close comparison tray"
                >
                    ×
                </CloseButton>
            </TopRow>

            <BottomRow>
                <Products>
                    {products.map((product, index) => {
                        const id = getProductId(product);
                        const title = getProductTitle(product);
                        const imageUrl = getProductImage(product);
                        const productUrl = getProductUrl(product);

                        return (
                            <ProductCard key={id || index}>
                                <RemoveButton
                                    type="button"
                                    aria-label={`Remove ${id} from comparison`}
                                    onClick={() => removeProduct(product)}
                                >
                                    ×
                                </RemoveButton>

                                {productUrl ? (
                                    <ProductLink
                                        href={productUrl}
                                        onClick={closeCompareModal}
                                    >
                                        <ProductLinkContent>
                                            <ProductThumbnail>
                                                {imageUrl && (
                                                    <img
                                                        src={imageUrl}
                                                        alt={title}
                                                    />
                                                )}
                                            </ProductThumbnail>

                                            <ProductInfo>
                                                <ProductTitle>
                                                    {title}
                                                </ProductTitle>

                                                <ProductCode>
                                                    {id}
                                                </ProductCode>
                                            </ProductInfo>
                                        </ProductLinkContent>
                                    </ProductLink>
                                ) : (
                                    <>
                                        <ProductThumbnail>
                                            {imageUrl && (
                                                <img
                                                    src={imageUrl}
                                                    alt={title}
                                                />
                                            )}
                                        </ProductThumbnail>

                                        <ProductInfo>
                                            <ProductTitle>
                                                {title}
                                            </ProductTitle>

                                            <ProductCode>
                                                {id}
                                            </ProductCode>
                                        </ProductInfo>
                                    </>
                                )}
                            </ProductCard>
                        );
                    })}
                </Products>

                <CompareButtonStyleWrapper>
                    <Button
                        variant="primary"
                        disabled={
                            products.length < 2 ||
                            isLoadingCompare
                        }
                        onClick={openCompare}
                    >
                        {isLoadingCompare
                            ? 'Loading...'
                            : 'Compare'}
                    </Button>
                </CompareButtonStyleWrapper>
            </BottomRow>
                </Wrapper>
            )}

            {isCompareModalOpen && (
                <ProductCompareModal
                    products={resolvedProducts}
                    onClose={closeCompareModal}
                    onRemoveProduct={removeComparedProduct}
                />
            )}
        </>,
        document.body
    );
};

export default CompareTray;

const Wrapper = styled.div`
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    z-index: ${({ theme }) => theme.zIndices['4']};

    width: min(850px, calc(100vw - 48px));
    padding: 16px;

    background: #fff;
    border: 1px solid #ddd;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

    @media (max-width: 768px) {
        left: 0;
        bottom: 0;
        transform: none;

        width: 100%;
        max-height: 70vh;
        padding: 12px;

        box-sizing: border-box;
        overflow-y: auto;
    }
`;

const TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const Title = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
`;

const Count = styled.span`
    font-size: 14px;
`;

const CloseButton = styled.button`
    border: 0;
    background: transparent;
    font-size: 22px;
    cursor: pointer;
`;

const BottomRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

const Products = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    flex: 1;
    gap: 8px;
    min-width: 0;

    @media (max-width: 768px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        width: 100%;
        gap: 4px;
    }
`;

const ProductCard = styled.div`
    position: relative;

    display: flex;
    flex-direction: column;

    min-width: 0;

    border: 1px solid #ddd;
    background: #fff;
`;

const ProductThumbnail = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    
    width: 100%;
    height: 110px;
    padding: 8px;

    box-sizing: border-box;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        cursor: pointer;
        background-color: white;
        
        transition: transform 0.15s ease;
    }

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.05);
        pointer-events: none;
    }
    @media (max-width: 768px) {
        height: 70px;
        padding: 4px;
    }
`;

const ProductInfo = styled.div`
    padding: 2px 10px 5px;
`;

const ProductTitle = styled.div`
    min-height: 17px;

    font-size: 13px;
    font-weight: 600;
    line-height: 15px;
    cursor: pointer;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    transition: text-decoration 0.15s ease;
`;

const ProductCode = styled.div`
    font-size: 12px;
    line-height: 16px;
    color: #555;
    cursor: pointer;
    
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProductLinkContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    height: 100%;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 2;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 24px;
    height: 24px;

    padding: 0;
    border: 0;
    background: rgba(255, 255, 255, 0.9);

    font-size: 18px;
    line-height: 1;
    cursor: pointer;
`;

const ProductLink = styled(Link)`
    display: block;
    flex: 1;
    min-width: 0;

    color: inherit;
    text-decoration: none;
    cursor: pointer;
    
    &,
    & * {
        cursor: pointer;
    }
    transform: translateY(0);

    transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease,
            transform 0.15s ease;

    &:hover {
        background: #f3f3f3;
        box-shadow: inset 0 0 0 2px #777;
        transform: translateY(-2px);
    }

    &:hover ${ProductTitle} {
        text-decoration: underline;
    }

    &:hover img {
        transform: scale(1.06);
    }
`;

const CompareButtonStyleWrapper = styled.div`
    min-width: 140px;

    @media (max-width: 768px) {
        width: 100%;

        button {
            width: 100%;
        }
    }
`;